/**
 * useDurableChatThreads — the reusable client hook for DURABLE, per-thread
 * concurrent chat. Produces the same shape ChatShell consumes (messages, status,
 * send, threads, …) but backed by durable server runs instead of a single
 * `useChat`, so:
 *   - each thread has its OWN streaming state (no bleed when you switch),
 *   - multiple threads can stream at once (one observe connection each),
 *   - a reload/reconnect resumes the thread's in-flight run (observe by runId).
 *
 * Backend contract (see trunk createDurableChatRoutes): POST {base}/send →
 * { runId }; GET {base}/observe?runId=&offset= → AI-SDK UI-message stream
 * (reconnectable); GET {base}/active-run?threadId= → SSE current runId.
 *
 * Thread CRUD is reused from the passed `transport` (list/get/create/rename/
 * delete) — only the streaming is durable here.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { readUIMessageStream } from 'ai'
// `@ai-sdk/provider-utils` no longer re-exports EventSourceParserStream (build
// fails: "not exported"). Import it from its canonical home instead.
import { EventSourceParserStream } from 'eventsource-parser/stream'
import type { ChatTransport, Thread } from 'tailwind-v4/blocks/chat'
import type {
  AgentDescriptor,
  AgentKey,
  Attachment,
  ChatMessage,
  ChatMessagePart,
  ChatStatus,
  ToolCall,
} from 'tailwind-v4/blocks/agent-chat'
import config from '@/config'
import { getToken } from '@/utils/AuthTokenHelper'

interface ThreadRuntime {
  messages: ChatMessage[]
  status: ChatStatus
  runId: string | null
  loaded: boolean
}

const emptyRuntime = (): ThreadRuntime => ({
  messages: [],
  status: 'idle',
  runId: null,
  loaded: false,
})

// Id for the optimistic assistant placeholder shown the instant a message is
// sent (so the skeleton appears immediately, before the run connects). The
// first real observe chunk replaces it.
const PENDING_ASSISTANT_ID = 'pending-assistant'

// ── AI-SDK stream helpers ────────────────────────────────────────────────────

/** fetch an observe SSE endpoint → ReadableStream<UIMessageChunk> for readUIMessageStream. */
async function openChunkStream(url: string): Promise<ReadableStream<unknown>> {
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${getToken()?.accessToken ?? ''}` },
  })
  if (!res.ok || !res.body) throw new Error(`observe failed: ${res.status}`)
  return res.body
    .pipeThrough(new TextDecoderStream())
    .pipeThrough(new EventSourceParserStream())
    .pipeThrough(
      new TransformStream<{ data: string }, unknown>({
        transform(event, controller) {
          const data = event?.data
          if (!data || data === '[DONE]') return
          try {
            controller.enqueue(JSON.parse(data))
          } catch {
            /* skip malformed keepalive/comment lines */
          }
        },
      })
    )
}

interface UiPart {
  type?: string
  text?: string
  toolCallId?: string
  toolName?: string
  input?: Record<string, unknown>
  args?: Record<string, unknown>
  output?: unknown
  result?: unknown
  state?: string
  errorText?: string
  url?: string
  mediaType?: string
  filename?: string
}
interface UiMessage {
  id?: string
  role?: string
  parts?: UiPart[]
}

const TOOL_STATE: Record<string, ToolCall['state']> = {
  'input-streaming': 'partial',
  'partial-call': 'partial',
  call: 'call',
  'output-available': 'result',
  result: 'result',
  complete: 'result',
}

/** Map a streaming AI-SDK UIMessage → the block's ChatMessage. */
function mapUiToChat(ui: UiMessage, fallbackId: string): ChatMessage {
  const parts: ChatMessagePart[] = []
  for (const p of ui.parts ?? []) {
    if (p.type === 'text' && typeof p.text === 'string') {
      if (p.text) parts.push({ type: 'text', text: p.text })
    } else if (
      p.type &&
      (p.type.startsWith('tool-') || p.type === 'dynamic-tool')
    ) {
      const name =
        p.toolName ??
        (p.type.startsWith('tool-') && p.type !== 'tool-invocation'
          ? p.type.slice('tool-'.length)
          : 'tool')
      parts.push({
        type: 'tool-call',
        toolCall: {
          id: p.toolCallId ?? `${fallbackId}-${name}`,
          name,
          args: p.input ?? p.args ?? {},
          state: TOOL_STATE[p.state ?? 'result'] ?? 'result',
          result: p.output ?? p.result,
          error: p.errorText,
        },
      })
    } else if ((p.type === 'file' || p.type === 'attachment') && p.url) {
      parts.push({
        type: 'attachment',
        attachment: {
          id: p.toolCallId ?? `${fallbackId}-att-${parts.length}`,
          filename: p.filename ?? 'attachment',
          mimeType: p.mediaType ?? 'application/octet-stream',
          url: p.url,
          uploadStatus: 'ready',
        },
      })
    }
  }
  return {
    id: ui.id ?? fallbackId,
    role: (ui.role as ChatMessage['role']) ?? 'assistant',
    parts,
    createdAt: new Date().toISOString(),
    status: 'streaming',
  }
}

// ── The hook ─────────────────────────────────────────────────────────────────

export interface UseDurableChatThreadsOptions {
  /** Reused for thread CRUD (list/get/create/rename/delete). */
  transport: ChatTransport
  /** Durable route base, e.g. '/agents/onboarding'. */
  basePath: string
  agent: AgentDescriptor
  activeThreadId: string | null
  onThreadChange?: (id: string | null) => void
  /** Same `appContext` blob the plain (non-durable) chat routes have long
   * shipped (see compose-app-context.ts) — trunk agents that read it
   * (knowledge-base's NAVIGATION AWARENESS block, marketing, wifi) get
   * "where in the app is the user right now". The durable `/send` route
   * previously never forwarded this at all. Recompute it in the caller
   * each render (e.g. from `useLocation()`) — read via a ref here so
   * `send`'s identity doesn't change on every render. */
  appContext?: Record<string, unknown>
}

export function useDurableChatThreads({
  transport,
  basePath,
  agent,
  activeThreadId,
  onThreadChange,
  appContext,
}: UseDurableChatThreadsOptions) {
  const [, force] = useReducer((n: number) => n + 1, 0)
  const appContextRef = useRef(appContext)
  appContextRef.current = appContext

  const threadsRef = useRef<Thread[]>([])
  const threadsLoadingRef = useRef(false)
  const messagesLoadingRef = useRef(false)
  const attachmentsRef = useRef<Attachment[]>([])
  // Per-thread runtime (messages/status/runId), keyed by threadId.
  const runtimeRef = useRef<Map<string, ThreadRuntime>>(new Map())
  // Runs we're already observing (avoid double-subscribe): runId → true.
  const observingRef = useRef<Set<string>>(new Set())

  const base = `${config.url.trunk}${basePath}`
  const rt = (id: string): ThreadRuntime => {
    let r = runtimeRef.current.get(id)
    if (!r) {
      r = emptyRuntime()
      runtimeRef.current.set(id, r)
    }
    return r
  }

  // ── Observe a run → stream its output into the thread's messages ──
  const observeRun = useCallback(
    async (threadId: string, runId: string, offset = 0) => {
      if (observingRef.current.has(runId)) return
      observingRef.current.add(runId)
      const r = rt(threadId)
      r.runId = runId
      r.status = 'streaming'
      force()
      try {
        const stream = await openChunkStream(
          `${base}/observe?runId=${encodeURIComponent(runId)}&offset=${offset}`
        )
        for await (const ui of readUIMessageStream({
          stream: stream as ReadableStream<never>,
        })) {
          const chat = mapUiToChat(ui as UiMessage, runId)
          // Replace the trailing assistant message for this run, else append.
          // Always assign a FRESH array so React/ChatShell re-render reliably
          // (in-place mutation keeps the same ref and can be skipped by memo).
          const msgs = r.messages
          const lastIdx = msgs.length - 1
          // Replace the trailing assistant message when it's this run's message
          // OR the optimistic placeholder (first chunk swaps it out).
          if (
            lastIdx >= 0 &&
            msgs[lastIdx].role === 'assistant' &&
            (msgs[lastIdx].id === chat.id ||
              msgs[lastIdx].id === PENDING_ASSISTANT_ID)
          ) {
            r.messages = [...msgs.slice(0, lastIdx), chat]
          } else {
            r.messages = [...msgs, chat]
          }
          force()
        }
        // Finalise the last assistant message (fresh array + object). If the
        // optimistic placeholder was never replaced (run produced nothing),
        // drop it so we don't leave an empty assistant bubble.
        const msgs = r.messages
        const lastIdx = msgs.length - 1
        if (lastIdx >= 0 && msgs[lastIdx].role === 'assistant') {
          if (
            msgs[lastIdx].id === PENDING_ASSISTANT_ID &&
            msgs[lastIdx].parts.length === 0
          ) {
            r.messages = msgs.slice(0, lastIdx)
          } else {
            r.messages = [
              ...msgs.slice(0, lastIdx),
              { ...msgs[lastIdx], status: 'complete' },
            ]
          }
        }
      } catch {
        r.status = 'error'
      } finally {
        observingRef.current.delete(runId)
        if (r.runId === runId) {
          r.runId = null
          r.status = 'idle'
        }
        force()
      }
    },
    [base]
  )

  // ── Load a thread's history + reconnect to any in-flight run ──
  const loadThread = useCallback(
    async (threadId: string) => {
      const r = rt(threadId)
      if (r.loaded) return
      messagesLoadingRef.current = true
      force()
      try {
        const { messages } = await transport.getThread(threadId)
        r.messages = messages as ChatMessage[]
        r.loaded = true
      } finally {
        messagesLoadingRef.current = false
        force()
      }
      // Reconnect to a live run for this thread, if any.
      try {
        const res = await fetch(
          `${base}/active-run?threadId=${encodeURIComponent(threadId)}`,
          {
            headers: {
              Authorization: `Bearer ${getToken()?.accessToken ?? ''}`,
            },
          }
        )
        // Read just the first active-run event to learn the current runId.
        if (res.body) {
          const reader = res.body
            .pipeThrough(new TextDecoderStream())
            .pipeThrough(new EventSourceParserStream())
            .getReader()
          const first = await reader.read()
          void reader.cancel()
          const data = (first.value as { data?: string } | undefined)?.data
          if (data) {
            const { runId } = JSON.parse(data) as { runId: string | null }
            if (runId) void observeRun(threadId, runId, 0)
          }
        }
      } catch {
        /* no active run / reconnect not available */
      }
    },
    [transport, base, observeRun]
  )

  // ── Threads list ──
  const refetchThreads = useCallback(async () => {
    threadsLoadingRef.current = true
    force()
    try {
      threadsRef.current = await transport.listThreads()
    } finally {
      threadsLoadingRef.current = false
      force()
    }
  }, [transport])

  useEffect(() => {
    void refetchThreads()
  }, [refetchThreads])

  // On active thread change: load it (history + reconnect). Other threads keep
  // their own observeRun loops running — no bleed, concurrent streams.
  useEffect(() => {
    if (activeThreadId) void loadThread(activeThreadId)
  }, [activeThreadId, loadThread])

  // ── Send ──
  const send = useCallback(
    async (text: string, sendAttachments?: Attachment[]) => {
      const list = sendAttachments ?? attachmentsRef.current
      const trimmed = text.trim()
      if (!trimmed && list.length === 0) return

      let tid = activeThreadId
      if (!tid) {
        const t = await transport.createThread({ title: trimmed.slice(0, 80) })
        tid = t.id
        threadsRef.current = [t, ...threadsRef.current]
        rt(t.id).loaded = true
        onThreadChange?.(t.id)
      }
      const threadId: string = tid! // guaranteed set above
      const r = rt(threadId)

      // Optimistic user message (with attachment parts so images render in-bubble).
      const userParts: ChatMessagePart[] = []
      if (trimmed) userParts.push({ type: 'text', text: trimmed })
      for (const a of list) {
        if (a.url) userParts.push({ type: 'attachment', attachment: a })
      }
      const nowIso = new Date().toISOString()
      r.messages = [
        ...r.messages,
        {
          id: `u-${Date.now()}`,
          role: 'user',
          parts: userParts,
          createdAt: nowIso,
          status: 'complete',
        },
        // Optimistic assistant placeholder → skeleton shows instantly while the
        // run connects. The first observe chunk replaces this (see observeRun).
        {
          id: PENDING_ASSISTANT_ID,
          role: 'assistant',
          parts: [],
          createdAt: nowIso,
          status: 'streaming',
        },
      ]
      r.status = 'submitting'
      attachmentsRef.current = []
      force()

      try {
        const res = await fetch(`${base}/send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()?.accessToken ?? ''}`,
          },
          body: JSON.stringify({
            threadId,
            messages: [{ role: 'user', content: trimmed }],
            attachments: list.map((a) => ({
              id: a.id,
              filename: a.filename,
              mimeType: a.mimeType,
            })),
            appContext: appContextRef.current,
          }),
        })
        const { runId } = (await res.json()) as { runId?: string }
        if (runId) void observeRun(threadId, runId, 0)
        else {
          // Send failed → drop the optimistic placeholder so the skeleton
          // doesn't hang around forever.
          r.messages = r.messages.filter((m) => m.id !== PENDING_ASSISTANT_ID)
          r.status = 'error'
          force()
        }
      } catch {
        r.messages = r.messages.filter((m) => m.id !== PENDING_ASSISTANT_ID)
        r.status = 'error'
        force()
      }
    },
    [activeThreadId, transport, base, observeRun, onThreadChange]
  )

  const stop = useCallback(() => {
    const threadId = activeThreadId
    if (!threadId) return
    const r = rt(threadId)
    const runId = r.runId
    r.status = 'idle'
    r.runId = null
    force()
    if (runId) {
      void fetch(`${base}/abort`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()?.accessToken ?? ''}`,
        },
        body: JSON.stringify({ threadId, runId }),
      }).catch(() => {})
    }
  }, [activeThreadId, base])

  // ── Thread CRUD passthrough ──
  const switchThread = useCallback(
    (id: string) => onThreadChange?.(id),
    [onThreadChange]
  )
  const createThread = useCallback(
    async (init?: { title?: string; agentKey?: AgentKey }) => {
      const t = await transport.createThread(init)
      threadsRef.current = [t, ...threadsRef.current]
      onThreadChange?.(t.id)
      return t
    },
    [transport, onThreadChange]
  )
  const renameThread = useCallback(
    async (id: string, title: string) => {
      await transport.renameThread(id, title)
      threadsRef.current = threadsRef.current.map((t) =>
        t.id === id ? { ...t, title } : t
      )
      force()
    },
    [transport]
  )
  const deleteThread = useCallback(
    async (id: string) => {
      await transport.deleteThread(id)
      threadsRef.current = threadsRef.current.filter((t) => t.id !== id)
      runtimeRef.current.delete(id)
      if (activeThreadId === id) onThreadChange?.(null)
      force()
    },
    [transport, activeThreadId, onThreadChange]
  )

  // ── Attachments ──
  const addAttachment = useCallback((a: Attachment) => {
    attachmentsRef.current = [...attachmentsRef.current, a]
    force()
  }, [])
  const removeAttachment = useCallback((id: string) => {
    attachmentsRef.current = attachmentsRef.current.filter((a) => a.id !== id)
    force()
  }, [])
  const clearAttachments = useCallback(() => {
    attachmentsRef.current = []
    force()
  }, [])

  const active = activeThreadId ? rt(activeThreadId) : null
  const activeThread = useMemo(
    () => threadsRef.current.find((t) => t.id === activeThreadId) ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeThreadId, threadsRef.current]
  )

  return {
    threads: threadsRef.current,
    activeThread,
    threadsLoading: threadsLoadingRef.current,
    messagesLoading: messagesLoadingRef.current,
    switchThread,
    createThread,
    renameThread,
    deleteThread,
    messages: active?.messages ?? [],
    status: active?.status ?? 'idle',
    progress: null,
    send,
    stop,
    attachments: attachmentsRef.current,
    addAttachment,
    removeAttachment,
    clearAttachments,
  }
}
